import { FC, useContext, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Button, DateRangePicker, H1, InfoBlock } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';

import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  CLUB_LOAD_BY_DAY,
  IClubLoadByDayOfWeekReportResponse,
  // IExportOverviewReportRequest,
  // IExportOverviewReportResponse,
  IOverviewReportItemsResponse,
  IOverviewReportRequest,
  IOverviewReportResponse,
  // OVERVIEW_EXPORT,
  OVERVIEW_REPORT,
  TOP_SOLD_OVERVIEW_ITEMS_REPORT,
} from 'schemas';
import { formatDateForServer } from 'utils';

import {
  Bonuses,
  ChartClubLoad,
  Clients,
  Expense,
  FinancialSummary,
  GoodsSummary,
  ItemsReport,
  OperatingSummary,
  PercentClubLoad,
  Revenue,
} from './components';
import s from './Review.module.scss';
import { useReportsData } from './useReportsData';

export const Review: FC = () => {
  const { t, lang } = useContext(LocalizationContext);

  const today = dayjs();

  const [queryVariables, setQueryVariables] =
    useState<IOverviewReportRequest | null>(null);

  console.log(queryVariables);

  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();

  const size = useWindowResize();
  const [error, setError] = useState<string | null>(null);

  const [
    getOverviewReport,
    { data: overviewReport, loading: overviewReportLoading },
  ] = useLazyQuery<IOverviewReportResponse, IOverviewReportRequest>(
    OVERVIEW_REPORT,
    {
      fetchPolicy: 'no-cache',
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  const [
    getOverviewItemsReport,
    { data: overviewItemsReport, loading: overviewReportItemsLoading },
  ] = useLazyQuery<IOverviewReportItemsResponse, IOverviewReportRequest>(
    TOP_SOLD_OVERVIEW_ITEMS_REPORT,
    {
      fetchPolicy: 'no-cache',
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  const [
    getClubLoadByDayReport,
    { data: clubLoadByDayReport, loading: clubLoadByDayReportLoading },
  ] = useLazyQuery<IClubLoadByDayOfWeekReportResponse, IOverviewReportRequest>(
    CLUB_LOAD_BY_DAY,
    {
      fetchPolicy: 'no-cache',
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  // const [exportOverviewReport] = useLazyQuery<
  //   IExportOverviewReportResponse,
  //   IExportOverviewReportRequest
  // >(OVERVIEW_EXPORT, {
  //   onCompleted: (response) => {
  //     if (!response?.exportOverviewReport) return;
  //
  //     open(response.exportOverviewReport, '_self');
  //   },
  // });
  //
  // const disabledExport = useMemo(() => {
  //   const fromDate = dayjs(queryVariables?.input?.start);
  //   const toDate = dayjs(queryVariables?.input?.end);
  //
  //   if (
  //     !clubLoadByDayReport?.clubLoadByDayOfWeekReport?.data &&
  //     !overviewItemsReport?.topSoldOverviewItemsReport?.data &&
  //     !overviewReport?.overviewReport?.data
  //   ) {
  //     return true;
  //   }
  //
  //   return toDate.diff(fromDate, 'day') < 27;
  // }, [
  //   startDate,
  //   endDate,
  //   clubLoadByDayReport,
  //   overviewReport,
  //   overviewItemsReport,
  // ]);

  const {
    financialData,
    bonusesData,
    goodsData,
    clientsData,
    operatingData,
    expenseData,
    clubLoadData,
  } = useReportsData(overviewReport, clubLoadByDayReport);

  const handleClick = (): void => {
    const variables = {
      input: {
        start: formatDateForServer(dayjs(startDate)),
        end: formatDateForServer(dayjs(endDate)),
      },
    };

    setQueryVariables(variables);

    getOverviewReport({
      variables,
    });
    getOverviewItemsReport({
      variables,
    });
    getClubLoadByDayReport({
      variables,
    });
  };

  return (
    <div className={s.container} data-onboarding="report-review-result">
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      <div
        className={s.controls}
        data-onboarding="report-review-generate"
        data-onboarding-report-generated={String(
          Boolean(
            overviewReport?.overviewReport &&
              overviewItemsReport?.topSoldOverviewItemsReport &&
              clubLoadByDayReport?.clubLoadByDayOfWeekReport
          )
        )}
      >
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={today.format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />

        <div className={s.right}>
          <Button
            onClick={() => {
              handleClick();
            }}
            disabled={overviewReportLoading}
          >
            {t('reports.generate')}
          </Button>
          {/*<Button*/}
          {/*  className={s.export}*/}
          {/*  iconName="IncomeIcon"*/}
          {/*  variant="outline"*/}
          {/*  onClick={() => {*/}
          {/*    if (!queryVariables) return;*/}

          {/*    exportOverviewReport({*/}
          {/*      variables: {*/}
          {/*        input: {*/}
          {/*          from: queryVariables.input.start,*/}
          {/*          to: queryVariables.input.end,*/}
          {/*        },*/}
          {/*      },*/}
          {/*    });*/}
          {/*  }}*/}
          {/*  disabled={*/}
          {/*    !queryVariables ||*/}
          {/*    clubLoadByDayReportLoading ||*/}
          {/*    overviewReportItemsLoading ||*/}
          {/*    overviewReportLoading ||*/}
          {/*    disabledExport*/}
          {/*  }*/}
          {/*>*/}
          {/*  {t('export.csv')}*/}
          {/*</Button>*/}
        </div>
      </div>

      <div className={s.content}>
        <div className={s.block}>
          <H1>{t('visitors.finance.title')}</H1>
          <FinancialSummary
            data={financialData?.summary}
            loading={overviewReportLoading}
          />
          {size.width > 960 ? (
            <div className={s.double_block}>
              <Revenue
                labels={financialData?.labels}
                values={financialData?.data[0]}
                sum={financialData?.summary[0].value}
                loading={overviewReportLoading}
              />
              <Expense
                labels={expenseData?.labels}
                values={expenseData?.data[0]}
                loading={overviewReportLoading}
              />
            </div>
          ) : (
            <InfoBlock
              className={s.mobile_info}
              title={t('mobile.placeholder')}
              iconName="ComputerIcon"
              color="blue"
            />
          )}
        </div>

        <div className={s.block}>
          <div
            className={cn(s.double_block, size.width < 960 && s.mobile_block)}
          >
            <Bonuses
              data={bonusesData?.summary}
              labels={bonusesData?.labels}
              values={bonusesData?.data[0]}
              sum={bonusesData?.summary[0].value}
              loading={overviewReportLoading}
            />
            <Clients
              data={clientsData?.summary}
              labels={clientsData?.labels}
              values={clientsData?.data[0]}
              sum={clientsData?.summary[0].value}
              loading={overviewReportLoading}
            />
          </div>
        </div>

        <div className={s.block}>
          <H1>{t('visitors.operating.indicators')}</H1>
          <OperatingSummary
            data={operatingData?.summary}
            loading={overviewReportLoading}
          />
          {size.width > 960 ? (
            <div className={s.double_block}>
              <PercentClubLoad
                data={operatingData?.summary}
                labels={operatingData?.labels}
                values={operatingData?.data[0]}
                sum={operatingData?.summary[0].value}
                loading={overviewReportLoading}
              />
              <ChartClubLoad
                data={clubLoadData}
                loading={clubLoadByDayReportLoading}
              />
            </div>
          ) : (
            <InfoBlock
              className={s.mobile_info}
              title={t('mobile.placeholder')}
              iconName="ComputerIcon"
              color="blue"
            />
          )}
        </div>

        <div className={s.block}>
          <H1>{t('category.goods')}</H1>
          <GoodsSummary
            data={goodsData?.summary}
            loading={overviewReportLoading}
          />
          {size.width > 960 ? (
            <div className={s.triple_block}>
              <ItemsReport
                data={overviewItemsReport}
                loading={overviewReportItemsLoading}
              />
            </div>
          ) : (
            <InfoBlock
              className={s.mobile_info}
              title={t('mobile.placeholder')}
              iconName="ComputerIcon"
              color="blue"
            />
          )}
        </div>
      </div>
    </div>
  );
};
